// src/Auth/SignUp.tsx

import React, { useState, CSSProperties, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CardHeader,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Link,
  TextField,
  Typography,
  Alert,
  Snackbar
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { SIGN_UP } from "../graphql/mutations";
import { useNavigate } from "react-router";
import Cookies from "js-cookie";
import TermsAndConditionsModal from "../Components/Modals/TermsAndConditionsModal";
const shareStockLogo = require('../assets/images/ShareStockLogoVertical2.png')

export default function SignUpPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
    termsAccepted: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [signUp] = useMutation(SIGN_UP);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(e.target.checked);
  };

  useEffect(() => {
    console.log("modal state change", modalOpen)
  }, [modalOpen])

  const validate = () => {
    let tempErrors: {
      email: string;
      name: string;
      password: string;
      companyName: string;
      termsAccepted: string;
    } = {
      companyName: "",
      name: "",
      email: "",
      password: "",
      termsAccepted: "",
    };
    tempErrors.email = formData.email ? "" : "Email is required.";
    tempErrors.email = /\S+@\S+\.\S+/.test(formData.email)
      ? ""
      : "Email is not valid.";
    tempErrors.password = formData.password ? "" : "Password is required.";
    tempErrors.companyName = formData.companyName
      ? ""
      : "Company name is required.";
    tempErrors.name = formData.name ? "" : "Name is required.";
    tempErrors.termsAccepted = termsAccepted
      ? ""
      : "You must accept the terms and conditions.";
    setErrors(tempErrors);

    return Object.values(tempErrors).every((x) => x === "");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        const { data } = await signUp({
          variables: {
            companyInput: { name: formData.companyName },
            userInput: {
              name: formData.name, // Assuming the part before "@" in the email is the user's name
              email: formData.email,
              password: formData.password,
            },
          },
        });
        console.log("data: ", data)
        if (data?.signUp.company?.id) {
          setSnackbarMessage("Email verification link sent! Please verify to continue.");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setFormData({
            companyName: "",
            name: "",
            email: "",
            password: "",
          });
          // Cookies.set("authToken", data.signUp.token, {
          //   secure: true, // Ensures the cookie is only sent over HTTPS
          //   sameSite: "Strict", // Prevents CSRF attacks
          //   expires: 1, // Cookie expires in 1 day (adjust as needed)
          // });
          // navigate("/landing"); // Navigate to the home page or any other page after login
        }
        // Handle success (e.g., redirect to another page, show a success message, etc.)
      } catch (error: any) {
        console.error("Error during signup:", error);

        let errorMessage = "An unexpected error occurred.";

        if (error.graphQLErrors?.length > 0) {
          // If the error came from your GraphQL API
          errorMessage = error.graphQLErrors[0].message;
        } else if (error.networkError) {
          // If the problem is network-related (like CORS or server down)
          errorMessage = "Network error: Please check your connection.";
        } else if (error.message) {
          // Fallback for generic error
          errorMessage = error.message;
        }

        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }

      setLoading(false);
    }
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // If we are logged in, automatically route to the landing page
  if (Cookies.get("authToken")!!) {
    navigate('/landing');
  }

  return (
    <Container maxWidth="sm" >

      <Box
        component="img"
        src={shareStockLogo}
        alt="Logo"
        sx={{
          width: "100%", // Set width
          height: "100%", // Maintain aspect ratio
          maxWidth: "200px",
          m: 1,
          mt: 1,
        }}
      />
      <Card elevation={2} sx={{ mb: 1 }}>
        <CardHeader
          title={
            <Container
              component="main"
              maxWidth="xs"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" align="center">
                Sign Up
              </Typography>
            </Container>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <form onSubmit={handleSubmit}>
            <FormGroup sx={{ gap: "0.5rem" }}>
              <FormControl style={styles.formControl} margin="dense">
                <FormLabel>Company Name</FormLabel>
                <TextField
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  variant="outlined"
                />
              </FormControl>
              <FormControl style={styles.formControl} margin="dense">
                <FormLabel>Name</FormLabel>
                <TextField
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  variant="outlined"
                />
              </FormControl>
              <FormControl style={styles.formControl} margin="dense">
                <FormLabel>Email</FormLabel>
                <TextField
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                />
              </FormControl>
              <FormControl style={styles.formControl} margin="dense">
                <FormLabel>Password</FormLabel>
                <TextField
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  variant="outlined"
                />
              </FormControl>
              <FormControl style={styles.formControl} margin="dense">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the{" "}
                      <span
                        style={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline" }}
                        onClick={() => setModalOpen(true)}
                      >
                        terms and conditions
                      </span>
                    </Typography>
                  }
                />
                {errors.termsAccepted && (
                  <Typography color="error" variant="body2">
                    {errors.termsAccepted}
                  </Typography>
                )}

                <TermsAndConditionsModal open={modalOpen} onClose={() => setModalOpen(false)} />
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                style={styles.button}
              >
                {
                  loading && (
                    <CircularProgress sx={{ mr: 2, color: 'white' }} size={30} />
                  )
                }
                <span>Sign Up</span>
              </Button>
              <Typography variant="body1" gutterBottom>
                <Link href="/login" color={"#000000"} underline="always">
                  I already have an account...
                </Link>
              </Typography>
            </FormGroup>
          </form>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} // Set the position to the bottom right
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

const styles: { [key: string]: CSSProperties } = {
  card: {
    marginTop: "2rem",
    padding: "2rem",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  formControl: {
    width: "100%",
    textAlign: "left",
  },
  button: {
    marginTop: "1rem",
  },
};
